// LAB7 · P1-B/C + R07-2 — Contexto de la habitación institucional.
// · Las indicaciones del residente se muestran SIEMPRE que sean pertinentes, también cuando está
//   solo en su habitación.
// · El aviso de identidad indeterminada y la verificación humana se reservan a las señales
//   REALMENTE no atribuibles (radar de habitación compartida). Un botón o wearable que identifica
//   a su portador no produce ese aviso aunque comparta habitación.
// · Nunca cambia la persona del caso, la cama ni la atribución original del sensor.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Users, BadgeCheck, BedSingle, DoorOpen } from "lucide-react";

export default function HabitacionCompartida({ detail, onIdentificarResidente, busy, readOnly }) {
  const [sel, setSel] = useState("");
  const residentes = detail?.residentes_habitacion || [];
  const ident = detail?.residente_identificado || null;
  const ind = detail?.indicaciones_residente || null;
  const atribuible = detail?.identidad_atribuible !== false;
  // P6 · «Compartida» SÓLO cuando el centro lo declaró (`habitacion_tipo`). Sin tipo, icono y
  // leyendas NEUTRALES: el número de residentes devueltos hoy no declara la distribución. Esto no
  // retira la lista de residentes ni la verificación humana, que dependen de la ATRIBUIBILIDAD de
  // la señal, nunca del conteo ni de «compartida».
  const tipo = detail?.habitacion_tipo || null;
  const compartida = tipo === "compartida";
  const rotulo = tipo === "compartida" ? "Habitación compartida"
    : tipo === "individual" ? "Habitación individual" : "Habitación";
  // C4 · este contexto es la consulta de HOY, no la fotografía del momento del caso.
  const ctxHab = detail?.contexto_habitacion || null;
  const rotuloTemporal = ctxHab?.rotulo_temporal
    || (detail?.estado === "cerrada" ? "Habitación hoy" : "Habitación actual");
  const candidatos = residentes.filter((r) => !r.es_persona_del_caso);
  if (residentes.length === 0 && !ind?.indicaciones && !detail?.persona_trasladada) return null;

  return (
    <section data-testid="v3-habitacion-compartida" data-atribuible={String(atribuible)}
      className={`rounded-xl border p-4 ${atribuible ? "border-slate-200 bg-white dark:border-slate-700 dark:bg-[#14243A]" : "border-amber-300/70 bg-amber-50/60 dark:bg-amber-900/10"}`}>
      <h3 className={`mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${atribuible ? "text-slate-500 dark:text-slate-300" : "text-amber-700 dark:text-amber-300"}`}>
        {compartida ? <Users strokeWidth={1.8} className="h-4 w-4" />
          : tipo === "individual" ? <BedSingle strokeWidth={1.8} className="h-4 w-4" />
            : <DoorOpen strokeWidth={1.8} className="h-4 w-4" />}
        {rotulo} · {detail?.zona_nombre || "Ubicación del centro"}
      </h3>

      {ctxHab?.vigencia === "actual" && (
        <p data-testid="v3-contexto-vigencia" className="mb-2 text-[11px] text-slate-400">
          {rotuloTemporal} · consultado el{" "}
          {ctxHab.consultado_en ? new Date(ctxHab.consultado_en).toLocaleString("es-MX") : "—"}
          {ind?.vigencia === "actual" ? " · indicaciones vigentes hoy" : ""}
        </p>
      )}

      {detail?.persona_trasladada && (
        <p data-testid="v3-persona-trasladada" className="mb-2 text-sm text-slate-600 dark:text-slate-300">
          La persona de este caso ya no pertenece a este centro. El caso se conserva como ocurrió y no
          se muestran sus datos actuales.
        </p>
      )}

      {!atribuible && !ident && (
        <p data-testid="v3-identidad-indeterminada" className="mb-2 text-sm text-slate-700 dark:text-slate-200">
          La señal no identifica a una sola persona. Verifica en la habitación quién la generó.
        </p>
      )}

      {atribuible && compartida && (
        <p data-testid="v3-identidad-atribuida" className="mb-2 text-sm text-slate-600 dark:text-slate-300">
          El equipo identifica a su portador. Comparte habitación, pero la señal sí es atribuible.
        </p>
      )}

      {atribuible && !compartida && candidatos.length > 0 && (
        <p data-testid="v3-otros-residentes-hoy" className="mb-2 text-sm text-slate-600 dark:text-slate-300">
          Hoy constan otros residentes en esta ubicación; la señal sí es atribuible.
        </p>
      )}

      {residentes.length > 0 && (
        <>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
            Residentes registrados hoy en esta ubicación
          </p>
          <ul className="space-y-1.5">
          {residentes.map((r) => (
            <li key={r.persona_id} data-testid={`v3-habitacion-residente-${r.persona_id}`} className="text-sm text-slate-700 dark:text-slate-200">
              <span className="font-semibold">{r.nombre || "Residente sin identificar"}</span>
              {r.cama_etiqueta && <span className="text-slate-500 dark:text-slate-400"> · Cama {r.cama_etiqueta}</span>}
              {r.es_persona_del_caso && <span className="ml-2 text-[11px] font-bold uppercase text-slate-400">Persona del caso</span>}
              {ident?.persona_id === r.persona_id && (
                <span data-testid="v3-residente-verificado" className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                  <BadgeCheck className="h-3 w-3" /> Verificado por el personal
                </span>
              )}
            </li>
          ))}
          </ul>
        </>
      )}

      {ident && (
        <p data-testid="v3-identidad-verificada" className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Identificado por {ident.verificado_por}. La atribución original de la señal y la cama no se modificaron.
        </p>
      )}

      {ind?.indicaciones && (
        <div data-testid="v3-indicaciones-residente" className="mt-3 rounded-lg bg-slate-50 dark:bg-[#192C47] p-2.5">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Indicaciones de {ind.nombre || "el residente"}</p>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{ind.indicaciones}</p>
        </div>
      )}

      {/* P6 · el control depende de la ATRIBUIBILIDAD de la señal y de que aún no haya
          identificación: nunca de «compartida» ni del número de residentes. */}
      {!atribuible && !ident && !readOnly && onIdentificarResidente && detail?.estado !== "cerrada" && candidatos.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="v3-identificar-residente-select" value={sel} onChange={(e) => setSel(e.target.value)}
            className="rounded-md border border-slate-200 bg-white dark:bg-[#192C47] px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
            <option value="">¿A quién verificó el personal?…</option>
            {candidatos.map((r) => <option key={r.persona_id} value={r.persona_id}>{r.nombre || r.persona_id}</option>)}
          </select>
          <Button data-testid="v3-identificar-residente-btn" disabled={busy || !sel}
            onClick={async () => { await onIdentificarResidente({ persona_id: sel }); setSel(""); }}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold">
            Registrar verificación humana
          </Button>
        </div>
      )}
    </section>
  );
}
